import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Flame, BookOpen, Target, TrendingUp, ArrowRight, Mic, Drum, Guitar, Piano, Users, Plus, Receipt, Lock, ClipboardList, DoorOpen, Award, Trophy, Star, Sparkles, Medal, BarChart3, Download, Gift, Copy, Crown, Bell, Mail } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { INSTRUMENTS, ALL_LESSONS, ALL_COURSES, GOSPEL_COURSES, getLessonContext, pickContinueLesson, earnedCertificates } from "@/data/curriculum";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardAlerts } from "@/components/GospelCourse";
import { PracticeLog } from "@/components/PracticeLog";
import { TakesTimeline } from "@/components/TakesTimeline";
import { TheoryStars } from "@/components/TheoryStars";
import { MyDevices } from "@/components/MyDevices";
import { VoiceRangeCard } from "@/components/VoiceRangeCard";
import { PracticeSchedule } from "@/components/PracticeSchedule";
import { ShareButtons, ReferralTracker, FreeLessonBanner, redeemFreeSession } from "@/components/ReferralKit";
import { QuizScoresCard } from "@/components/QuizScoresCard";
import { ParentEmailCard } from "@/components/ParentEmailCard";
import { GameScoresCard } from "@/components/GameScoresCard";
import { PerfectStarsCard } from "@/components/PerfectStarsCard";
import { CountAlongTracker } from "@/components/CountAlongTracker";
import { PupilHomeworkCalendar } from "@/components/PupilHomeworkCalendar";
import { GameOfWeekCard } from "@/components/GameOfWeekCard";

const INST_ICON = { singers: Mic, drummers: Drum, guitarists: Guitar, pianists: Piano };
const COURSE_TITLE = { ...Object.fromEntries(ALL_COURSES.map((c) => [c.key, c.title])), ...Object.fromEntries(GOSPEL_COURSES.map((c) => [c.key, c.title])) };

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-6" data-testid={testId}>
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
      <Icon size={18} />
    </span>
    <p className="mt-4 font-serif text-4xl text-foreground">{value}</p>
    <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
  </div>
);

const AssignedToMe = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/student/assignments").then(({ data }) => setItems(data.assignments || [])).catch(() => setItems([])); }, []);

  if (!items || items.length === 0) return null;

  return (
    <div className="mt-12" data-testid="assignments-section">
      <h2 className="font-serif text-2xl">Assigned to you</h2>
      <div className="mt-4 space-y-3">
        {items.map((a) => {
          const to = (a.type === "lesson" || a.type === "countalong") && a.lesson_id ? `/lessons/${a.lesson_id}` : `/courses/${a.instrument_id}/${a.course_id}`;
          let dueLabel = null, dueCls = "bg-secondary text-muted-foreground";
          if (a.due_date) {
            const days = Math.ceil((new Date(a.due_date + "T00:00:00").getTime() - Date.now()) / 86400000);
            const nice = new Date(`${a.due_date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
            if (days < 0) { dueLabel = `Overdue · ${nice}`; dueCls = "bg-red-500/15 text-red-600 dark:text-red-400"; }
            else if (days <= 3) { dueLabel = days === 0 ? "Due today" : `Due soon · ${nice}`; dueCls = "bg-primary/10 text-primary"; }
            else { dueLabel = `Due ${nice}`; }
          }
          return (
            <Link
              key={a.id}
              to={to}
              data-testid={`assignment-${a.id}`}
              className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><ClipboardList size={18} /></span>
                <div>
                  <p className="font-medium text-foreground">{a.title}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>From {a.class_name || "your class"}</span>
                    {dueLabel && <span className={`rounded-full px-2 py-0.5 text-[10px] ${dueCls}`}>{dueLabel}</span>}
                  </p>
                </div>
              </div>
              <ArrowRight size={16} className="shrink-0 text-primary" />
            </Link>
          );
        })}
      </div>
      <PupilHomeworkCalendar items={items} />
    </div>
  );
};

const MyClasses = () => {
  const [classes, setClasses] = useState([]);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  const load = () => api.get("/student/classes").then(({ data }) => setClasses(data.classes || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const join = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/classes/join", { join_code: code.trim().toUpperCase() });
      toast.success(`Joined "${data.name}"`);
      setCode("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not join class");
    } finally {
      setJoining(false);
    }
  };

  const leave = async (id, name) => {
    try {
      await api.delete(`/student/classes/${id}`);
      toast.success(`Left "${name}"`);
      load();
    } catch (e) {
      toast.error("Could not leave class");
    }
  };

  return (
    <div className="mt-12" data-testid="my-classes-section">
      <h2 className="font-serif text-2xl">Your classes</h2>
      <form onSubmit={join} className="mt-4 flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 text-muted-foreground"><Users size={18} className="text-primary" /> <span className="text-sm">Have a class code from your teacher?</span></div>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter join code"
          data-testid="join-code-input"
          maxLength={12}
          className="border-border bg-background font-mono tracking-widest sm:max-w-[220px]"
        />
        <Button type="submit" disabled={joining} data-testid="join-class-button" className="rounded-full bg-primary text-foreground hover:bg-primary/90">
          <Plus size={16} className="mr-1" /> {joining ? "Joining…" : "Join class"}
        </Button>
      </form>
      {classes.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="joined-classes">
          {classes.map((c) => (
            <div key={c.id} className="flex items-start justify-between gap-3 rounded-2xl border border-border bg-card p-5" data-testid={`joined-class-${c.id}`}>
              <div>
                <p className="font-serif text-lg">{c.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">Taught by {c.teacher_name || "your teacher"}</p>
              </div>
              <button
                onClick={() => leave(c.id, c.name)}
                data-testid={`leave-class-${c.id}`}
                className="flex shrink-0 items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-red-500 hover:text-red-600 dark:text-red-400"
              >
                <DoorOpen size={13} /> Leave
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Purchases = ({ purchases }) => {
  const [receipts, setReceipts] = useState([]);
  useEffect(() => { api.get("/payments/history").then(({ data }) => setReceipts(data.receipts || [])).catch(() => {}); }, []);

  return (
    <div className="mt-12" data-testid="purchases-section">
      <h2 className="font-serif text-2xl">Your courses & receipts</h2>
      {purchases.length === 0 ? (
        <div className="mt-4 flex flex-col items-start gap-3 rounded-2xl border border-dashed border-border bg-card p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="no-purchases">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Lock size={18} /></span>
            <p className="text-sm text-muted-foreground">You haven't unlocked any courses yet. Choose an instrument to get started.</p>
          </div>
          <Link to="/pricing" data-testid="purchases-see-pricing"><Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">See pricing</Button></Link>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="owned-courses">
          {purchases.map((key) => (
            <div key={key} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5" data-testid={`owned-course-${key}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary"><BookOpen size={18} /></span>
              <div>
                <p className="font-medium text-foreground">{COURSE_TITLE[key] || key}</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">Fully unlocked</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {receipts.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card" data-testid="receipts-list">
          <div className="flex items-center gap-2 border-b border-border px-5 py-3">
            <Receipt size={16} className="text-primary" />
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Receipts</p>
          </div>
          <table className="w-full text-left text-sm">
            <tbody>
              {receipts.map((r) => (
                <tr key={r.session_id} className="border-b border-border last:border-0" data-testid={`receipt-${r.session_id}`}>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3">{r.course_keys.map((k) => COURSE_TITLE[k] || k).join(", ")}</td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{r.tier}</td>
                  <td className="px-5 py-3 text-right font-mono font-medium text-foreground">£{Number(r.amount || 0).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function WeeklyRecap() {
  const [days, setDays] = useState([]);
  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("jt_practice") || "null") || {};
      const hist = raw.history || {};
      const out = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        const key = d.toISOString().slice(0, 10);
        out.push({ key, label: d.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1), mins: Math.round((hist[key] || 0) / 60) });
      }
      setDays(out);
    } catch (e) {}
  }, []);
  const max = Math.max(1, ...days.map((d) => d.mins));
  const total = days.reduce((a, d) => a + d.mins, 0);
  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="weekly-recap">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-lg">This week's practice</h3>
        <span className="font-mono text-xs uppercase tracking-widest text-primary" data-testid="weekly-total">{total} min</span>
      </div>
      <div className="mt-4 flex items-end gap-2" style={{ height: 96 }}>
        {days.map((d) => (
          <div key={d.key} className="flex flex-1 flex-col items-center gap-1" data-testid={`weekly-bar-${d.key}`}>
            <div className="flex w-full flex-1 items-end">
              <div
                className="w-full rounded-t-md bg-primary/80 transition-all"
                style={{ height: `${Math.max(4, Math.round((d.mins / max) * 80))}%` }}
                title={`${d.mins} min`}
              />
            </div>
            <span className="font-mono text-[10px] text-muted-foreground">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


const LEVEL_ORDER = ["Beginner", "Intermediate", "Advanced"];

function weekMinutes() {
  try {
    const raw = JSON.parse(localStorage.getItem("jt_practice") || "null") || {};
    const hist = raw.history || {};
    let total = 0;
    for (let i = 0; i < 7; i++) {
      const key = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      total += Math.round((hist[key] || 0) / 60);
    }
    return total;
  } catch (e) {
    return 0;
  }
}

function ProgressReport({ name, mastery, lessons, total, streak, badgesEarned, badgesTotal, certs }) {
  const download = () => {
    const mins = weekMinutes();
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const stat = (x, label, cx) => `
      <text x="${cx}" y="360" text-anchor="middle" fill="#F97316" font-family="Georgia, serif" font-size="72" font-weight="bold">${esc(x)}</text>
      <text x="${cx}" y="405" text-anchor="middle" fill="#A1A1AA" font-family="monospace" font-size="20" letter-spacing="2">${esc(label)}</text>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 600">
      <rect width="1000" height="600" fill="#0B0B0D"/>
      <rect x="24" y="24" width="952" height="552" fill="none" stroke="#F97316" stroke-width="3"/>
      <text x="500" y="110" text-anchor="middle" fill="#F97316" font-family="monospace" font-size="20" letter-spacing="8">THE JT HUB</text>
      <text x="500" y="180" text-anchor="middle" fill="#FAFAFA" font-family="Georgia, serif" font-size="44" font-weight="bold">${esc(name)}'s progress</text>
      <text x="500" y="222" text-anchor="middle" fill="#A1A1AA" font-family="Georgia, serif" font-size="22" font-style="italic">Learning to read music, one lesson at a time</text>
      ${stat(mastery + "%", "MASTERY", 200)}
      ${stat(lessons + "/" + total, "LESSONS", 400)}
      ${stat(streak, "DAY STREAK", 600)}
      ${stat(mins, "MIN THIS WK", 800)}
      <text x="500" y="480" text-anchor="middle" fill="#FAFAFA" font-family="Georgia, serif" font-size="26">${badgesEarned}/${badgesTotal} achievements · ${certs} certificate${certs === 1 ? "" : "s"} earned</text>
      <text x="500" y="540" text-anchor="middle" fill="#71717A" font-family="monospace" font-size="18">thejthub.com</text>
    </svg>`;
    const svg64 = btoa(unescape(encodeURIComponent(svg)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 2000; canvas.height = 1200;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0B0B0D";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const a = document.createElement("a");
      a.download = "JTHub-progress-report.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <div className="mt-4 flex flex-col items-start justify-between gap-3 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center" data-testid="progress-report">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Sparkles size={18} /></span>
        <div>
          <p className="font-serif text-lg">Share your progress</p>
          <p className="text-sm text-muted-foreground">Download a shareable card with your streak, mastery and this week's practice.</p>
        </div>
      </div>
      <Button onClick={download} data-testid="progress-report-download" className="rounded-full bg-primary text-foreground hover:bg-primary/90">
        <Download size={16} className="mr-2" /> Download report
      </Button>
    </div>
  );
}

function Analytics({ scores, completed }) {
  const instAgg = {};
  Object.entries(scores).forEach(([key, s]) => {
    const m = /^lesson-(\d+)$/.exec(key);
    if (!m || !s || !s.total) return;
    const ctx = getLessonContext(Number(m[1]));
    if (!ctx) return;
    const id = ctx.instrument.id;
    instAgg[id] = instAgg[id] || { best: 0, total: 0 };
    instAgg[id].best += s.best;
    instAgg[id].total += s.total;
  });
  const quizBars = INSTRUMENTS.map((inst) => {
    const a = instAgg[inst.id];
    return { id: inst.id, label: inst.label, pct: a && a.total ? Math.round((a.best / a.total) * 100) : null };
  });

  const levelAgg = { Beginner: { done: 0, total: 0 }, Intermediate: { done: 0, total: 0 }, Advanced: { done: 0, total: 0 } };
  ALL_LESSONS.forEach((l) => {
    const lv = levelAgg[l.level] ? l.level : "Beginner";
    levelAgg[lv].total += 1;
    if (completed.has(l.id)) levelAgg[lv].done += 1;
  });

  const anyQuiz = quizBars.some((b) => b.pct !== null);

  return (
    <div className="mt-12" data-testid="analytics-section">
      <div className="flex items-center gap-2">
        <BarChart3 size={18} className="text-primary" />
        <h2 className="font-serif text-2xl">Your analytics</h2>
      </div>
      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="analytics-quiz">
          <h3 className="font-serif text-lg">Quiz accuracy by instrument</h3>
          <p className="mt-1 text-xs text-muted-foreground">Average of your best scores across each instrument's lesson quizzes.</p>
          <div className="mt-5 space-y-4">
            {!anyQuiz ? (
              <p className="text-sm text-muted-foreground" data-testid="analytics-quiz-empty">Take a lesson quiz to see your accuracy here.</p>
            ) : quizBars.map((b) => (
              <div key={b.id} data-testid={`analytics-quiz-${b.id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{b.label}</span>
                  <span className="font-mono text-xs text-primary">{b.pct === null ? "—" : `${b.pct}%`}</span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${b.pct || 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="analytics-levels">
          <h3 className="font-serif text-lg">Lessons completed by level</h3>
          <p className="mt-1 text-xs text-muted-foreground">How far you've progressed through beginner, intermediate and advanced material.</p>
          <div className="mt-5 space-y-4">
            {LEVEL_ORDER.map((lv) => {
              const a = levelAgg[lv];
              const pct = a.total ? Math.round((a.done / a.total) * 100) : 0;
              return (
                <div key={lv} data-testid={`analytics-level-${lv.toLowerCase()}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{lv}</span>
                    <span className="font-mono text-xs text-primary">{a.done}/{a.total}</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


function InviteAndEarn() {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/referral/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!stats || !stats.eligible) return null;
  const link = stats.code ? `${window.location.origin}/pricing?ref=${stats.code}` : "";
  const generate = async () => {
    setBusy(true);
    try { await api.post("/referral/generate"); await load(); toast.success("Your referral code is ready to share"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate a code"); } finally { setBusy(false); }
  };
  const copy = (text, msg) => navigator.clipboard.writeText(text).then(() => {
    setCopied(true); toast.success(msg); setTimeout(() => setCopied(false), 1500);
  });
  const share = () => navigator.share ? navigator.share({ title: "The JT Hub", text: `Use my code ${stats.code} to get £50 off a JT Hub course!`, url: link }).catch(() => {}) : copy(link, "Invite link copied");
  const redeem = async () => { setBusy(true); if (!(await redeemFreeSession())) setBusy(false); };
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="invite-earn">
      <div className="flex items-center gap-2"><Gift size={18} className="text-primary" /><h3 className="font-serif text-lg">Refer a friend after booking</h3></div>
      <p className="mt-1 text-sm text-muted-foreground">You've booked a course — now refer it to someone else. When they sign up with your code they save £50: your friend pays <strong className="text-foreground">£450</strong> for a 10-lesson course or <strong className="text-foreground">£800</strong> for a 2-course bundle (teachers: £350 per class licence). When they pay, you get a <strong className="text-foreground">free 1:1 lesson with Jason</strong>.</p>
      {!stats.code ? (
        <Button onClick={generate} disabled={busy} data-testid="referral-generate" className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Generate my referral code</Button>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-xl border-2 border-dashed border-primary/60 px-4 py-2 font-mono text-lg font-bold tracking-widest text-primary" data-testid="referral-code">{stats.code}</span>
            <Button onClick={() => copy(stats.code, "Code copied")} data-testid="referral-copy" variant="outline" className="rounded-full border-border"><Copy size={14} className="mr-1.5" /> {copied ? "Copied" : "Copy code"}</Button>
            <Button onClick={share} data-testid="referral-share" variant="outline" className="rounded-full border-border">Share link</Button>
          </div>
          <Input readOnly value={link} data-testid="referral-link" className="mt-3 border-border bg-background text-xs" onFocus={(e) => e.target.select()} />
          <ShareButtons code={stats.code} link={link} />
          <ReferralTracker stats={stats} />
        </>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
        <span data-testid="referral-conversions"><span className="font-mono text-lg text-primary">{stats.conversions}</span> <span className="text-muted-foreground">friends paid</span></span>
        {stats.free_sessions > 0 && (
          <Button onClick={redeem} disabled={busy} data-testid="referral-redeem" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Sparkles size={14} className="mr-1.5" /> Book your free 1:1 ({stats.free_sessions} available)
          </Button>
        )}
      </div>
    </div>
  );
}

function PracticeReminderToggle() {
  const { user, reloadUser } = useAuth();
  const [on, setOn] = useState(!!user?.practice_reminders);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const next = !on;
    setOn(next); setSaving(true);
    try {
      await api.patch("/me/preferences", { practice_reminders: next });
      toast.success(next ? "Weekly practice reminders on" : "Practice reminders off");
      if (reloadUser) reloadUser();
    } catch (e) {
      setOn(!next); toast.error("Could not update your preference");
    }
    setSaving(false);
  };
  return (
    <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-6" data-testid="practice-reminder-card">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Bell size={18} /></span>
        <div>
          <p className="font-serif text-lg">Practice reminders</p>
          <p className="text-sm text-muted-foreground">Get a friendly email nudge when your streak is about to break.</p>
        </div>
      </div>
      <button
        onClick={toggle} disabled={saving} data-testid="practice-reminder-toggle"
        role="switch" aria-checked={on}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-secondary"}`}
      >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );
}

function EmailReminderToggle() {
  const { user, reloadUser } = useAuth();
  const [on, setOn] = useState(user?.email_reminders !== false);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const next = !on;
    setOn(next); setSaving(true);
    try {
      await api.patch("/me/preferences", { email_reminders: next });
      toast.success(next ? "Email reminders on" : "Email reminders off");
      if (reloadUser) reloadUser();
    } catch (e) {
      setOn(!next); toast.error("Could not update your preference");
    }
    setSaving(false);
  };
  return (
    <div className="mt-4 flex items-center justify-between rounded-2xl border border-border bg-card p-6" data-testid="email-reminder-card">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Mail size={18} /></span>
        <div>
          <p className="font-serif text-lg">Email reminders</p>
          <p className="text-sm text-muted-foreground">Occasional tips and reminders from Jason, like your refer-a-friend reward.</p>
        </div>
      </div>
      <button onClick={toggle} disabled={saving} data-testid="email-reminder-toggle" role="switch" aria-checked={on} aria-label="Email reminders"
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-secondary"}`}>
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );
}

function Leaderboard({ currentDisp }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => { api.get("/leaderboard").then(({ data }) => setEntries(data.entries || [])).catch(() => setEntries([])); }, []);
  if (entries === null) return null;
  const rows = entries
    .map((e) => ({ ...e, certs: earnedCertificates(e.completed_lessons).length }))
    .sort((a, b) => b.certs - a.certs || b.lessons_done - a.lessons_done || b.streak - a.streak)
    .slice(0, 10);
  if (rows.length === 0) return null;
  const medal = ["text-yellow-500", "text-slate-400", "text-amber-700"];
  return (
    <div className="mt-12" data-testid="leaderboard-section">
      <div className="flex items-center gap-2"><Crown size={18} className="text-primary" /><h2 className="font-serif text-2xl">Certificate leaderboard</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Top learners by course certificates earned. Keep going to climb the board!</p>
      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
        {rows.map((r, i) => {
          const isMe = currentDisp && r.name === currentDisp;
          return (
            <div key={`${r.name}-${i}`} data-testid={`leaderboard-row-${i}`}
              className={`flex items-center justify-between gap-3 border-b border-border px-5 py-3 last:border-0 ${isMe ? "bg-primary/10" : ""}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center font-mono text-sm">
                  {i < 3 ? <Medal size={18} className={medal[i]} /> : <span className="text-muted-foreground">{i + 1}</span>}
                </span>
                <span className="font-medium text-foreground">{r.name}{isMe && <span className="ml-2 text-xs text-primary">(you)</span>}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-primary" title="Certificates"><Award size={14} /> {r.certs}</span>
                <span className="hidden font-mono text-xs text-muted-foreground sm:inline">{r.lessons_done} lessons</span>
                <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground"><Flame size={13} /> {r.streak}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, progress, purchases } = useAuth();
  const completed = new Set(progress?.completed_lessons || []);
  const scores = progress?.quiz_scores || {};
  const _nameParts = (user?.name || "").trim().split(/\s+/).filter(Boolean);
  const leaderboardDisp = _nameParts.length ? _nameParts[0] + (_nameParts.length > 1 ? ` ${_nameParts[_nameParts.length - 1][0]}.` : "") : "";

  const totalLessons = ALL_LESSONS.length;
  const mastery = Math.round((completed.size / totalLessons) * 100);
  const quizCount = Object.keys(scores).length;
  const avgQuiz = quizCount
    ? Math.round(Object.values(scores).reduce((a, s) => a + (s.best / s.total) * 100, 0) / quizCount)
    : 0;

  const nextLesson = pickContinueLesson(progress?.completed_lessons || [], progress?.last_lesson_id);
  const nextCtx = nextLesson ? getLessonContext(nextLesson.id) : null;

  const streak = progress?.streak_count || 0;
  const practicedToday = progress?.last_active === new Date().toISOString().slice(0, 10);
  const practiceMsg = practicedToday
    ? `Nice — you've practiced today. Streak: ${streak} day${streak === 1 ? "" : "s"}. Keep the momentum going!`
    : streak > 0
    ? `You're on a ${streak}-day streak — practice one lesson today to keep it alive.`
    : "Practice a little every day. Even one lesson starts your streak.";

  const anyQuizPerfect = Object.values(scores).some((s) => s.total && s.best === s.total);
  const instrumentComplete = INSTRUMENTS.some((inst) =>
    inst.courses.flatMap((c) => c.lessons).every((l) => completed.has(l.id))
  );
  const badges = [
    { id: "first", icon: Star, title: "First Steps", desc: "Complete your first lesson", earned: completed.size >= 1 },
    { id: "ten", icon: Medal, title: "Getting Serious", desc: "Complete 10 lessons", earned: completed.size >= 10 },
    { id: "half", icon: Award, title: "Halfway Hero", desc: "Complete 40 lessons", earned: completed.size >= 40 },
    { id: "ace", icon: Target, title: "Full Marks", desc: "Score 100% on any quiz", earned: anyQuizPerfect },
    { id: "streak", icon: Flame, title: "On Fire", desc: "Reach a 7-day streak", earned: streak >= 7 },
    { id: "inst", icon: Trophy, title: "Instrument Master", desc: "Finish every lesson for one instrument", earned: instrumentComplete },
    { id: "all", icon: Sparkles, title: "Grand Finale", desc: `Complete all ${totalLessons} lessons`, earned: completed.size >= totalLessons },
  ];
  const earnedCount = badges.filter((b) => b.earned).length;

  const earnedIdsKey = badges.filter((b) => b.earned).map((b) => b.id).join(",");
  useEffect(() => {
    if (!user || !progress) return;
    const earnedIds = badges.filter((b) => b.earned).map((b) => b.id);
    const storeKey = `jt_badges_${user.id || user.email}`;
    let seen;
    try { seen = JSON.parse(localStorage.getItem(storeKey) || "null"); } catch (e) { seen = null; }
    if (seen === null || !Array.isArray(seen)) {
      localStorage.setItem(storeKey, JSON.stringify(earnedIds));
      return;
    }
    const fresh = earnedIds.filter((id) => !seen.includes(id));
    if (fresh.length) {
      fresh.forEach((id) => {
        const b = badges.find((x) => x.id === id);
        if (b) toast.success(`Achievement unlocked — ${b.title}!`, { description: b.desc });
      });
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.7 }, colors: ["#B45309", "#FEF3C7", "#1C1917", "#F59E0B"] });
      localStorage.setItem(storeKey, JSON.stringify(earnedIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnedIdsKey, user, progress]);

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10 lg:py-14">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Your progress</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">
          Welcome back, {user?.name?.split(" ")[0] || "musician"}.
        </h1>
        <DashboardAlerts
          eligibleForGospel={(purchases || []).some((k) => ALL_COURSES.some((c) => c.key === k))}
          ownsGospel={(purchases || []).some((k) => GOSPEL_COURSES.some((c) => c.key === k))}
        />
        <FreeLessonBanner />

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={TrendingUp} value={`${mastery}%`} label="Overall mastery" testId="stat-mastery" />
          <Stat icon={Flame} value={progress?.streak_count || 0} label="Day streak" testId="stat-streak" />
          <Stat icon={BookOpen} value={`${completed.size}/${totalLessons}`} label="Lessons done" testId="stat-lessons" />
          <Stat icon={Target} value={`${avgQuiz}%`} label="Avg quiz score" testId="stat-quiz" />
        </div>

        {/* practice reminder */}
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-border bg-secondary px-5 py-4" data-testid="practice-reminder">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Flame size={16} />
          </span>
          <p className="text-sm text-muted-foreground">{practiceMsg}</p>
        </div>

        <WeeklyRecap />

        <ProgressReport
          name={user?.name?.split(" ")[0] || "Musician"}
          mastery={mastery}
          lessons={completed.size}
          total={totalLessons}
          streak={streak}
          badgesEarned={earnedCount}
          badgesTotal={badges.length}
          certs={earnedCertificates([...completed]).length}
        />

        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <InviteAndEarn />
          <PracticeReminderToggle />
          <EmailReminderToggle />
        </div>

        <Leaderboard currentDisp={leaderboardDisp} />

        {/* continue */}
        {nextLesson && nextCtx ? (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl bg-secondary p-6 sm:flex-row sm:items-center"
          >
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-primary">Continue where you left off</p>
              <p className="mt-1 text-sm text-muted-foreground">{nextCtx.instrument.label} · {nextCtx.course.tagline}</p>
              <p className="mt-2 font-serif text-2xl text-foreground">Lesson {nextCtx.number}: {nextLesson.title}</p>
            </div>
            <Link to={`/lessons/${nextLesson.id}`} data-testid="continue-lesson">
              <Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">
                Continue <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="mt-8 rounded-2xl bg-secondary p-6 text-center">
            <p className="font-serif text-2xl text-foreground">🎼 You've completed every lesson. Bravo!</p>
            <Link to="/trainer"><Button className="mt-4 rounded-full bg-primary text-foreground">Keep sharp in the Trainer</Button></Link>
          </div>
        )}

        {/* per-instrument progress */}
        <PracticeLog />
        <TakesTimeline />
        <TheoryStars />
        <PracticeSchedule instrumentId={getLessonContext(Number(progress?.last_lesson_id))?.instrument.id} />
        <MyDevices />
        <h2 className="mt-12 font-serif text-2xl">Your instruments</h2>
        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          {INSTRUMENTS.map((inst) => {
            const Icon = INST_ICON[inst.id] || Mic;
            const instLessons = [...inst.courses, ...(inst.miniCourses || []).filter((m) => (purchases || []).includes(`${inst.id}:${m.id}`))].flatMap((c) => c.lessons);
            const instDone = instLessons.filter((l) => completed.has(l.id)).length;
            return (
              <div key={inst.id} className="rounded-2xl border border-border bg-card p-6" data-testid={`dash-instrument-${inst.id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary">
                    <Icon size={20} />
                  </span>
                  <div>
                    <p className="font-serif text-xl">{inst.label}</p>
                    <p className="text-xs text-muted-foreground">{instDone}/{instLessons.length} lessons complete</p>
                  </div>
                </div>
                <div className="mt-5 space-y-4">
                  {[...inst.courses, ...(inst.miniCourses || []).filter((m) => (purchases || []).includes(`${inst.id}:${m.id}`))].map((course) => {
                    const doneCount = course.lessons.filter((l) => completed.has(l.id)).length;
                    const pct = Math.round((doneCount / course.lessons.length) * 100);
                    const courseComplete = doneCount === course.lessons.length;
                    return (
                      <div key={course.id}>
                        <Link
                          to={`/courses/${inst.id}/${course.id}`}
                          data-testid={`dash-course-${inst.id}-${course.id}`}
                          className="block rounded-xl border border-border bg-background p-4 transition-colors hover:border-primary"
                        >
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{course.tagline}</span>
                            <span className="font-mono text-xs text-primary">{doneCount}/{course.lessons.length}</span>
                          </div>
                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </Link>
                        {courseComplete && (
                          <Link
                            to={`/certificate/${inst.id}/${course.id}`}
                            data-testid={`dash-certificate-${inst.id}-${course.id}`}
                            className="mt-2 flex items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
                          >
                            <Award size={14} /> Get your certificate
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* achievements */}
        <Analytics scores={scores} completed={completed} />
        <QuizScoresCard />
        <GameOfWeekCard />
        <PerfectStarsCard />
        <CountAlongTracker />
        <GameScoresCard />
        <ParentEmailCard />

        <div className="mt-12 flex items-end justify-between">
          <h2 className="font-serif text-2xl">Achievements</h2>
          <span className="font-mono text-xs uppercase tracking-widest text-primary" data-testid="badges-earned-count">{earnedCount}/{badges.length} earned</span>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="badges-grid">
          {badges.map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.id}
                data-testid={`badge-${b.id}${b.earned ? "-earned" : "-locked"}`}
                className={`rounded-2xl border p-5 transition-colors ${
                  b.earned ? "border-primary bg-card" : "border-dashed border-border bg-background opacity-70"
                }`}
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                  b.earned ? "bg-secondary text-primary" : "bg-muted text-muted-foreground"
                }`}>
                  <Icon size={20} />
                </span>
                <p className={`mt-3 font-serif text-lg ${b.earned ? "text-foreground" : "text-muted-foreground"}`}>{b.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{b.desc}</p>
                {b.earned && <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Unlocked</p>}
              </div>
            );
          })}
        </div>

        <Link to="/worksheets" className="mt-8 flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary" data-testid="dashboard-worksheets-link">
          <span className="font-serif text-lg">Worksheet Library</span>
          <span className="text-sm text-muted-foreground">Print every lesson worksheet and manuscript paper in one place</span>
          <span className="ml-auto text-sm text-primary">Open →</span>
        </Link>
        <Link to="/terms" className="mt-4 flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary" data-testid="dashboard-terms-link">
          <span className="font-serif text-lg">My musical terms</span>
          <span className="text-sm text-muted-foreground">Every term from your completed lessons, plus flashcards to revise them</span>
          <span className="ml-auto text-sm text-primary">Revise →</span>
        </Link>
        <VoiceRangeCard />
        <Purchases purchases={purchases || []} />
        <AssignedToMe />
        <MyClasses />
      </div>
    </div>
  );
}
